<template>
  <div class="offcanvas offcanvas-end" style="width: min(680px, 100vw);" id="OffcanvasManageBranches" ref="offcanvas" data-bs-backdrop="static">
    <div class="bg-body-secondary border offcanvas-header py-2">
      <span>
        <span class="badge me-2" :style="{ backgroundColor: platform.color || '#6B7280' }">{{ platform.name }}</span>
        {{ $t('_delivery.Manage branches') }}
      </span>
      <button type="button" style="font-size: small;" class="btn-close shadow-none text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <div class="border-bottom px-3 py-2 text-muted" style="font-size: 12px;">
        <i class="fa-solid fa-circle-info me-1"/>{{ $t('_delivery.GP left empty = use the default rate of the channel') }}
        ({{ Number(info.default_commission_rate).toFixed(2) }}%)
      </div>
      <table class="table table-sm align-middle mb-0">
        <thead>
          <tr class="text-muted small">
            <th class="ps-3">{{ $t('_delivery.Branch') }}</th>
            <th style="width: 90px;">{{ $t('_delivery.Enabled') }}</th>
            <th style="width: 120px;">{{ $t('_delivery.GP %') }}</th>
            <th v-if="info.has_pickup" style="width: 120px;">{{ $t('_delivery.Pickup GP %') }}</th>
            <th style="width: 160px;" class="pe-3">{{ $t('_delivery.Store ID on platform') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.branch_id">
            <td class="ps-3">{{ item.name }}</td>
            <td>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" v-model="item.is_active">
              </div>
            </td>
            <td>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="form-control form-control-sm text-end shadow-none"
                v-model="item.commission_rate"
                :placeholder="Number(info.default_commission_rate).toFixed(2)"
                :disabled="!item.is_active"
              >
            </td>
            <td v-if="info.has_pickup">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="form-control form-control-sm text-end shadow-none"
                v-model="item.pickup_commission_rate"
                :placeholder="info.pickup_commission_rate !== null && info.pickup_commission_rate !== undefined ? Number(info.pickup_commission_rate).toFixed(2) : Number(info.default_commission_rate).toFixed(2)"
                :disabled="!item.is_active"
              >
            </td>
            <td class="pe-3">
              <input
                type="text"
                class="form-control form-control-sm shadow-none"
                v-model.trim="item.external_store_id"
                :placeholder="$t('optional')"
                :disabled="!item.is_active"
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="bg-light border-top justify-content-center modal-footer py-2" v-if="!loading && hasPermission">
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        @click="handleSave"
      />
      <button type="button" data-bs-dismiss="offcanvas" class="border-dark-subtle btn-sm btn btn-light me-2 ms-2">
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { DeliveryPlatforms } from '@/services/endpoint/delivery-platforms.service';

/**
 * จัดการรายสาขาจากฝั่ง platform (docs ฝั่ง api: delivery-platform-pricing.md)
 * - opt-in: toggle ปิดอยู่ = สาขานั้นไม่เห็น platform · GP ว่าง = ใช้ default ของ platform หลัก
 * - เป็น mirror ของแท็บ Delivery ในหน้าตั้งค่าสาขา แต่ดูจากมุม platform (rows = สาขา)
 */
export default {
  props: {
    platform: { type: Object, required: true },
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      info: { default_commission_rate: 0, pickup_commission_rate: null, has_pickup: false },
      items: []
    }
  },
  components: { PlaceholderContent, Ladda, AccessDenied },
  methods: {
    async fetchData() {
      this.loading = true;
      await DeliveryPlatforms.getBranches(this.platform.id).then(res => {
        const data = res.data.data;
        this.info = {
          default_commission_rate: data.default_commission_rate,
          pickup_commission_rate: data.pickup_commission_rate,
          has_pickup: !!data.has_pickup
        };
        this.items = (data.branches || []).map(b => ({
          branch_id: b.branch_id,
          name: b.name,
          is_active: !!b.is_active,
          commission_rate: (b.commission_rate !== null && b.commission_rate !== undefined) ? b.commission_rate : '',
          pickup_commission_rate: (b.pickup_commission_rate !== null && b.pickup_commission_rate !== undefined) ? b.pickup_commission_rate : '',
          external_store_id: b.external_store_id || ''
        }));
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) this.hasPermission = false;
        else if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      });
      this.loading = false;
    },
    async handleSave() {
      if (this.loadingSave) return;
      this.loadingSave = true;
      const payload = {
        items: this.items.map(item => ({
          branch_id: item.branch_id,
          is_active: !!item.is_active,
          commission_rate: item.commission_rate === '' ? null : Number(item.commission_rate),
          pickup_commission_rate: item.pickup_commission_rate === '' ? null : Number(item.pickup_commission_rate),
          external_store_id: item.external_store_id || null
        }))
      };
      await DeliveryPlatforms.updateBranches(this.platform.id, payload).then(() => {
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Success!'));
        this.fetchData();
      }).catch(err => {
        if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      }).finally(() => {
        this.loadingSave = false;
      });
    }
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', () => this.fetchData());
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.manageBranches.open = false;
    });
  }
}
</script>
